/**
 * pages/Settings.tsx
 *
 * Page des paramètres utilisateur.
 * Updated: adoption du layout deux colonnes via SettingsSection (titre + description
 * à gauche, card à droite), inspiré de GitHub, Stripe et Linear.
 * Chaque section dispose d'un titre et d'une description contextuelle dans la colonne gauche.
 * La SaveBar s'affiche en position fixe uniquement en cas de modifications non enregistrées.
 * Updated: suppression des séparateurs border-t entre sections - space-y-10 suffit.
 * Updated: ajout des props logo (logoFile, logoPreviewUrl, handleLogoChange) transmises
 * à SettingsProfileSection pour la gestion du logo de profil.
 * Updated: ajout de la section Notifications avec deux toggles indépendants (notification
 * immédiate et résumé quotidien). Scroll automatique vers #notifications si présent dans l'URL.
 * Updated: scroll automatique vers #stamp - dépendance sur `loading` ajoutée pour que le scroll
 * s'exécute après le chargement du profil (le ref est null tant que le spinner est affiché).
 * Updated Apr 2026: ajout du scroll automatique vers #profile-logo pour les liens
 * provenant de la DashboardOnboardingCard.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import SettingsSection from '../components/settings/SettingsSection';
import SettingsProfileSection from '../components/settings/SettingsProfileSection';
import SettingsSecuritySection from '../components/settings/SettingsSecuritySection';
import SettingsDangerZone from '../components/settings/SettingsDangerZone';
import SettingsSaveBar from '../components/settings/SettingsSaveBar';
import SettingsNotificationsSection from '../components/settings/SettingsNotificationsSection';
import { useSettingsProfile } from '../hooks/useSettingsProfile';
import { useToast } from '../contexts/ToastContext';

export default function Settings() {
  const { showToast } = useToast();
  const location = useLocation();
  const notificationsRef = useRef<HTMLDivElement>(null);
  const stampRef = useRef<HTMLDivElement>(null);

  const {
    profile, loading, saving, hasChanges,
    logoFile, logoPreviewUrl, handleLogoChange,
    notificationPrefs, updateNotificationPref,
    updateField, saveProfile, resetChanges,
  } = useSettingsProfile();

  useEffect(() => {
    if (loading) return;

    let delay = 0;
    let targetEl: HTMLElement | null = null;

    if (location.hash === '#notifications' && notificationsRef.current) {
      targetEl = notificationsRef.current;
      delay = 100;
    } else if (location.hash === '#stamp' && stampRef.current) {
      targetEl = stampRef.current;
      delay = 150;
    } else if (location.hash === '#profile-logo') {
      targetEl = document.getElementById('profile-logo');
      delay = 150;
    }

    if (!targetEl) return;

    const el = targetEl;
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, delay);
    return () => clearTimeout(timer);
  }, [location.hash, loading]);

  const handleSave = async () => {
    const { error } = await saveProfile();
    if (error) {
      showToast(error, 'error');
    } else {
      showToast('Paramètres mis à jour avec succès.', 'success');
    }
  };

  return (
    <AppLayout
      breadcrumbs={[{ label: 'Qollect', path: '/dashboard' }, { label: 'Paramètres' }]}
    >
      <div className="space-y-10 pb-20">
        <SettingsSection
          title="Profil"
          description="Informations personnelles et identité de votre compte."
        >
          <SettingsProfileSection
            profile={profile}
            loading={loading}
            onUpdateField={updateField}
            logoFile={logoFile}
            logoPreviewUrl={logoPreviewUrl}
            onLogoChange={handleLogoChange}
            stampRef={stampRef}
          />
        </SettingsSection>

        <SettingsSection
          title="Sécurité"
          description="Gérez votre mot de passe pour protéger votre compte."
        >
          <SettingsSecuritySection />
        </SettingsSection>

        <div ref={notificationsRef} id="notifications">
          <SettingsSection
            title="Notifications"
            description="Choisissez comment et quand recevoir vos notifications par email lorsqu'un client complète un formulaire."
          >
            <SettingsNotificationsSection
              prefs={notificationPrefs}
              loading={loading}
              onUpdatePref={updateNotificationPref}
            />
          </SettingsSection>
        </div>

        <SettingsSection
          title="Supprimer mon compte"
          description="Action définitive et irréversible. Toutes vos données seront supprimées."
          variant="danger"
        >
          <SettingsDangerZone />
        </SettingsSection>
      </div>

      <SettingsSaveBar
        visible={!!hasChanges}
        saving={saving}
        onSave={handleSave}
        onCancel={resetChanges}
      />
    </AppLayout>
  );
}
